import { FC } from "react"
import { useTranslation } from "react-i18next"

import ArticleSelectionTable from "components/ArticleSelection/ArticleSelectionTable"
import useArticleSelection, { IArticleSelectionItem, IArticleSelectionValue } from "components/ArticleSelection/useArticleSelection"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"

interface IInsertConfigurationModalProps {
    articles: IArticleSelectionItem[]
    onClose: () => void
    onSave: (params: IArticleSelectionValue) => void
}

/**
 * Dialog that turns a request configuration into text for a text box.
 *
 * The user picks which articles contribute and, per article, whether its assets are merged away
 * and whether features are printed; the selection behaviour lives in `useArticleSelection`.
 * Saving is blocked while no article is selected — `ConfirmModal` disables its confirm button on a
 * falsy `onConfirm`.
 */
export const InsertConfigurationModal: FC<IInsertConfigurationModalProps> = ({
    onClose,
    onSave,
    articles,
}) => {
    const { t } = useTranslation()
    const selection = useArticleSelection(articles)

    const handleSave = () => {
        onSave(selection.value)
        onClose()
    }

    return (
        <ConfirmModal
            title={t("textModal.insertConfigurationText")}
            onClose={onClose}
            onConfirm={selection.hasSelection ? handleSave : undefined}
            width="952px"
            confirmButtonLabel={t("common.save")}>
            <ArticleSelectionTable articles={articles} selection={selection} />
        </ConfirmModal>
    )
}
